// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {useEffect, useMemo, useState} from 'react';

import YAML from '../../../../services/yaml';
import {type TemplateVariable} from '../../../generated/graphql';
import {CodeEditor} from '../CodeEditor';
import {sortTemplateVariablesByConfig} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import ResourceVariableForm from '../DefaultResourceConfig/ResourceVariableForm';
import {type JsonObject} from '../utilTypes';
import * as S from './JobDrawerContentInner.styles';
import {substituteVariables} from './utils';

export const JobDrawerAdditionalServiceConfigEditor = ({
  additionalServiceName,
  serviceConfig,
  templateVariables,
  config,
  updateAdditionalService,
  displayMode,
  updateVariable,
  variableValues,
}: {
  additionalServiceName: string;
  serviceConfig: JsonObject;
  templateVariables: TemplateVariable[];
  config: string;
  displayMode: string;
  updateAdditionalService: (update: {
    configString?: string;
    isValid?: boolean;
    displayType?: string;
  }) => void;
  updateVariable: (name: string, value: string | number) => void;
  variableValues: Record<string, number | string>;
}) => {
  const sortedTemplateVariables = useMemo(() => {
    return sortTemplateVariablesByConfig(
      JSON.stringify(serviceConfig),
      templateVariables
    );
  }, [serviceConfig, templateVariables]);

  const variantYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const variantJson = displayMode === 'json' ? 'secondary' : 'ghost';
  const [showResourceConfig, setShowResourceConfig] = useState(false);

  const onResourceConfigChange = (value: string) => {
    updateAdditionalService({
      configString: value,
      isValid: true,
    });
  };

  useEffect(() => {
    const substitutedConfig = substituteVariables(
      serviceConfig,
      variableValues
    );

    const configString =
      displayMode === 'yaml'
        ? YAML.stringify(substitutedConfig, {indent: 2})
        : JSON.stringify(substitutedConfig, null, 2);

    if (configString !== config) {
      updateAdditionalService({
        configString,
      });
    }
  }, [
    displayMode,
    variableValues,
    serviceConfig,
    updateAdditionalService,
    config,
  ]);

  return (
    <>
      <div className="mb-4 mt-16 flex items-center">
        <div className="text-[1rem] font-semibold leading-[2rem]">
          {additionalServiceName} Configurations
        </div>
      </div>
      {sortedTemplateVariables.length > 0 &&
        sortedTemplateVariables.map((variable, i) => (
          <ResourceVariableForm
            key={i}
            name={variable.name}
            schema={variable.schema}
            description={variable.description}
            value={variableValues[variable.name]}
            updateVariable={updateVariable}
          />
        ))}
      <div className="mb-4 mt-16 flex items-center">
        <div>Complete {additionalServiceName} configurations</div>
        <S.ToggleConfig>
          {showResourceConfig && (
            <Button
              variant={variantYaml}
              onClick={() =>
                updateAdditionalService({
                  displayType: 'yaml',
                })
              }>
              YAML
            </Button>
          )}
          {showResourceConfig && (
            <Button
              variant={variantJson}
              onClick={() =>
                updateAdditionalService({
                  displayType: 'json',
                })
              }>
              JSON
            </Button>
          )}
          <Button
            data-test="show-resource-config"
            variant="ghost"
            onClick={() => {
              setShowResourceConfig(!showResourceConfig);
            }}>
            {showResourceConfig ? 'Hide' : 'Show'}
          </Button>
        </S.ToggleConfig>
      </div>
      {showResourceConfig && (
        <CodeEditor
          value={config}
          language={displayMode}
          onChange={onResourceConfigChange}
          readOnly={sortedTemplateVariables.length > 0}
        />
      )}
    </>
  );
};
